import { getCurrentUser } from 'library/firebase/fb.auth.utils';
import SuperFetch from 'library/helpers/superFetch';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { authActions } from 'reduxStore/auth/slice';
import { Notification } from 'zComponents/atoms';
import { organisationActions } from './slice';

export function* getOrganisations() {
	try {
		// Dynamically import superFetch
		const { default: superFetch } = yield call(() => import('library/helpers/superFetch'));

		// Get the current user from Firebase
		const currentUser = yield call(getCurrentUser);
		const fBPersonID = currentUser.uid;

		if (!fBPersonID) {
			throw new Error('No FBPersonID found in the current profile');
		}

		// Fetch organisation details
		// ? Fetch the organisation details from the API using the FBPersonID
		// ? Using the override parameter to force the API to return the workspace details for the user's first time fetch.
		// ? As user login has just occurred, the workspace details are not yet fetched, we have to override the workspace fetch to get the workspaces details.
		const response = yield call(superFetch.get, '/organisations/orgByPerson', {}, { fbPersonID: fBPersonID }, 1);

		// Handle response
		if (response.statusCode !== 200) {
			throw new Error(response.error?.message || 'Failed to fetch workspaces');
		}

		// Dispatch success action with retrieved data
		yield put(organisationActions.loadOrganisationsSuccess(response.data));
	} catch (error) {
		// Dispatch error action with the error message
		yield put(organisationActions.loadOrganisationsError(error.message));
	}
}

export function* getOrganisationsSuccess() {
	// Do something after the workspaces have been successfully loaded

	// Get the selected profile
	yield put(authActions.getUserProfile());
}

// This saga handles the creation of a new organisation
export function* createOrganisationSaga({ payload }) {
	try {
		// 1. Get data from profile (first user info)
		const { firstName, lastName, email } = yield select((state) => state.Auth.selectedProfile);
		const { organisationName } = payload;

		// 2. Prepare the body with multiple people (even if it's just one for now)
		const createOrgBody = {
			name: organisationName,
			people: [
				{
					firstName,
					lastName,
					email
				}
			]
		};

		// 3. Send to API
		yield SuperFetch.post('/organisations', createOrgBody, {}, 1);

		// 4. Success Notification
		yield put(organisationActions.createOrganisationSuccess());
		Notification('success', 'Welcome to Zezamii!', 'Organisation has been created successfully.');
	} catch (error) {
		yield put(organisationActions.createOrganisationError(error.message));
		Notification('error', 'There is an error creating organisation! Please try again later.');
	}
}

export function* deleteOrganisationSaga({ payload }) {
	const { deletedOrgID } = payload;

	try {
		// Call the API to delete the organisation
		yield SuperFetch.delete(`/organisations`);
		// Dispatch success action
		yield put(organisationActions.deleteOrganisationSuccess({ deletedOrgID }));
		Notification('success', 'Organisation deleted successfully.');
	} catch (error) {
		yield put(organisationActions.deleteOrganisationError(error.message));
		Notification('error', 'Failed to delete organisation. Please try again later.');
	}
}

export function* deleteOrganisationSuccess({ payload }) {
	const { deletedOrgID } = payload;

	// Get the current list of organisations from the store
	const { data } = yield select((state) => state.Organisations);

	if (!data || data.length === 0) {
		// No organisations left — fetch fresh user profile
		yield put(authActions.getUserProfile());
		return;
	}

	// Find the index of the organisation that was just deleted
	const indexDeleted = data.findIndex((org) => org.orgID === deletedOrgID);

	// If there was only one organisation and it was deleted
	if (data.length === 1 && indexDeleted !== -1) {
		yield put(authActions.getUserProfile());
		return;
	}

	// If the deleted organisation was the first one in the list
	if (indexDeleted === 0 && data.length > 1) {
		// Switch to the second organisation
		yield put(authActions.switchProfile(data[1].orgID));
	} else {
		// Otherwise, switch to the first organisation
		yield put(authActions.switchProfile(data[0].orgID));
	}
}
export function* updateOrganisationSaga({ payload }) {
	const { orgID, values } = payload;
	try {
		const response = yield SuperFetch.patch(`/organisations/${orgID}`, values);

		// Check if the response indicates an error
		if (response.statusCode === 200) {
			Notification('success', 'Organisation updated successfully.');
			yield put(organisationActions.updateOrganisationSuccess({ orgID, data: values }));
		} else {
			throw Error(response.error?.message || response.error || 'Failed to update device field.');
		}
	} catch (error) {
		yield put(organisationActions.updateOrganisationError(error.message));
		Notification('error', 'Failed to update organisation. Please try again later.');
	}
}

// The main export for the saga
export default function* settingsSagas() {
	yield takeLatest(organisationActions.loadOrganisations, getOrganisations);
	yield takeLatest(organisationActions.loadOrganisationsSuccess, getOrganisationsSuccess);
	yield takeLatest(organisationActions.createOrganisation, createOrganisationSaga);
	yield takeLatest(organisationActions.createOrganisationSuccess, getOrganisations);
	yield takeLatest(organisationActions.deleteOrganisation, deleteOrganisationSaga);
	yield takeLatest(organisationActions.deleteOrganisationSuccess, deleteOrganisationSuccess);
	yield takeLatest(organisationActions.updateOrganisation, updateOrganisationSaga);
}
// This is the main saga that will be exported
